/**
 * Phase 1/6 admin UI — viewer for backend/errors.py's error_logs collection.
 * POST /api/errors/report has been live since Phase 1; no client (mobile/web)
 * has been wired to call it yet (that's the Phase 6 client-side task per the
 * roadmap), so this page will show nothing until that wiring lands. It still
 * needs to exist now so the moment reports start flowing in, they're visible.
 */
import React, { useEffect, useState, useCallback } from "react";
import PageHeader from "../components/ui/PageHeader";
import EmptyState from "../components/ui/EmptyState";
import { SkeletonTable } from "../components/ui/Skeleton";
import { RefreshCw, AlertOctagon } from "lucide-react";
import { apiListErrors, apiUpdateErrorStatus } from "../lib/cloudSync";

const STATUS_TABS = ["open", "investigating", "resolved"];

const STATUS_COLORS = {
  open: "bg-danger/10 text-danger border-danger/30",
  investigating: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30",
  resolved: "bg-success/10 text-success border-success/30",
};

const ErrorLog = () => {
  const [status, setStatus] = useState("open");
  const [source, setSource] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async (s, src) => {
    setLoading(true);
    setError("");
    try {
      const data = await apiListErrors(s || undefined, src || undefined);
      setItems(data?.items || []);
    } catch (e) {
      setError(e?.message || "Failed to load error log");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(status, source); }, [status, source, load]);

  const setItemStatus = async (id, newStatus) => {
    setBusyId(id);
    try {
      await apiUpdateErrorStatus(id, newStatus);
      setItems((prev) => prev.filter((it) => it.id !== id));
    } catch (e) {
      setError(e?.message || "Failed to update item");
    } finally {
      setBusyId(null);
    }
  };

  return (
      <div className="max-w-5xl space-y-6">
          <PageHeader
            title="Error Log"
            subtitle="Reports from POST /api/errors/report. Empty until a client is wired to send them."
            actions={
              <button
                onClick={() => load(status, source)}
                className="text-xs text-celestial-muted hover:text-celestial-text flex items-center gap-1.5 shrink-0"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Refresh
              </button>
            }
          />

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-1">
              {STATUS_TABS.map((s) => (
                <button
                  key={s}
                  onClick={() => setStatus(s)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${
                    status === s
                      ? "bg-glow-mid/15 text-celestial-text"
                      : "text-celestial-muted hover:text-celestial-text"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
            <input
              value={source}
              onChange={(e) => setSource(e.target.value)}
              placeholder="Filter by source (e.g. ai_request)…"
              className="px-3 py-1.5 rounded-lg bg-surface/50 border border-celestial-border/20 text-celestial-text placeholder:text-celestial-muted/40 focus:outline-none focus:border-glow-mid/50 text-xs w-64"
            />
          </div>

          {error && (
            <div className="text-sm text-danger bg-danger/10 border border-danger/30 rounded-xl px-3 py-2">{error}</div>
          )}

          {loading ? (
            <SkeletonTable rows={4} />
          ) : (
          <div className="card-celestial rounded-2xl divide-y divide-celestial-border/10">
            {items.length === 0 ? (
              <EmptyState icon={AlertOctagon} title={`No ${status} errors.`} description="Reports sent from mobile or web clients will appear here." />
            ) : (
              items.map((item) => (
                <div key={item.id} className="p-4 flex items-start justify-between gap-4">
                  <div className="space-y-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${STATUS_COLORS[item.status] || ""}`}>
                        {item.status}
                      </span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-surface/60 text-celestial-muted">
                        {item.source}
                      </span>
                      {item.screen && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-surface/60 text-celestial-muted">
                          {item.screen}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-celestial-text break-words">{item.message}</div>
                    {item.stack && (
                      <pre className="text-[11px] text-celestial-muted/70 bg-surface/40 rounded-lg p-2 max-h-32 overflow-auto">
                        {item.stack}
                      </pre>
                    )}
                    <div className="text-[11px] text-celestial-muted/60">
                      {item.app_version && <>v{item.app_version} · </>}
                      {item.device && <>{item.device} · </>}
                      {item.created_at ? new Date(item.created_at).toLocaleString() : "—"}
                    </div>
                  </div>
                  <select
                    disabled={busyId === item.id}
                    value={item.status}
                    onChange={(e) => setItemStatus(item.id, e.target.value)}
                    className="text-xs bg-surface/50 border border-celestial-border/20 rounded-lg px-2 py-1.5 text-celestial-text shrink-0"
                  >
                    {STATUS_TABS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              ))
            )}
          </div>
          )}
      </div>
  );
};

export default ErrorLog;
